import React, { useId } from 'react'
import { Box } from '@mui/material'
import { keyframes } from '@emotion/react'

import { isVeraOwnedSurface } from 'utils/subdomain'

/**
 * The grainy-gradient ground the auth surfaces sit on — VERA'S OWN surfaces only.
 *
 * Three blurred purple blobs drifting slowly over a flat base, with a static
 * film-grain overlay on top. Reproduced from the approved prototype panel
 * ("ORIGINAL motion, coarse grain") — every value here is that panel's, not a
 * re-derivation, so treat them as fixed unless the design is revisited. What
 * has changed is their FORM: the panel's lengths are now carried as ratios of
 * the frame it was drawn on, not as the px it happened to write them in.
 *
 * ## ⚠️ Why every length is a RATIO — the bug this shape prevents
 *
 * The prototype is a 375-wide phone, and its CSS says `340px`. Transcribed as
 * `340` that reads correctly at 375 and is wrong everywhere else: at 1024 the
 * same blob is 33% of the frame where it was 91%, and because the blobs are
 * ALSO positioned by percentage offsets of a container that grows with the
 * viewport, they shrink and fly apart at the same time. MEASURED at 1440×900
 * before this was fixed: two of the three blobs had their entire diameter
 * outside the viewport (centres at x = −492 and x = +1976), so a desktop user
 * saw one faint blob and no composition at all.
 *
 * So: no px literals below. Sizes, travel and blur are ratios of `FRAME`
 * resolved through `SCALE()`, and positions are CENTRES expressed as fractions
 * of the viewport. Both are evaluated so that 375×812 renders byte-for-byte
 * what it rendered before — the approved phone composition is the anchor, and
 * every other frame is derived from it.
 *
 * The one length that deliberately does NOT scale is the grain; its own note
 * explains why, and that asymmetry is a decision, not an omission.
 *
 * It is a SHARED component rather than CSS copied into each page because there
 * are two call sites in this app (working-conventions §36 puts the threshold at
 * two): the `/sign-in` page and the `/signup` page. Two copies of a keyframe set
 * is two places a colour change has to land, and the drift between them would be
 * invisible until someone put the screens side by side.
 *
 * ## ⚠️ THIS COMPONENT SELF-GATES, AND THAT IS THE POINT
 *
 * `apps/app` is WHITE-LABEL — the same bundle serves Vera's own consumer app AND
 * a brand's certificate experience, both on brand subdomains and embedded
 * directly into brands' storefronts. Purple is VERA's brand, so painting it onto
 * a customer's storefront is exactly the wrong outcome.
 *
 * So this renders `null` unless `isVeraOwnedSurface()` says the page is Vera's
 * own. The check lives HERE, not only at the call sites, because a gate a caller
 * has to remember is a gate that gets forgotten — the referrer-state prop that
 * 35 of 37 call sites omitted (working-conventions §42) is the standing example.
 * A new call site is safe by construction; the worst a forgetful one can do is
 * fail to show a background on a page that was entitled to one.
 *
 * Call sites still need `isVeraOwnedSurface()` themselves for one reason only:
 * to decide whether to paint their OWN opaque background (see Usage). That is a
 * duplicate read of a pure, synchronous function, not a second source of truth.
 *
 * ## Usage
 *
 * Renders a `position: fixed` layer at `zIndex: 0`. It paints its own base
 * colour, so a host that renders it must NOT also paint an opaque background —
 * an opaque page shell sits on top and hides it entirely. The host's content
 * needs `position: relative` + `zIndex: 1` to stack above it.
 *
 * Because the gate can turn this off, the host must keep its original
 * background for the gated-off case. The pattern at both call sites is:
 *
 *     const veraOwned = isVeraOwnedSurface()
 *     ...
 *     {veraOwned && <AuthBackdrop />}
 *     <Shell sx={{ bgcolor: veraOwned ? undefined : 'background.default' }}>
 *
 * so a brand page renders byte-for-byte what it rendered before this existed.
 *
 * ## Why light is the default and dark is class-gated
 *
 * `apps/app` has no runtime dark mode today. `index.css` declares the
 * `#vera-shadow-root.dark` custom-property block, but nothing ever adds that
 * class, and `settings/theme.ts` never sets `palette.mode`, so MUI's text
 * colours are light-mode always.
 *
 * That is exactly why this keys off the `.dark` CLASS and deliberately does NOT
 * key off `@media (prefers-color-scheme: dark)`. Keying on the media query
 * would flip the ground to near-black for every user whose phone is in dark
 * mode while the form's text stayed near-black with it — dark text on a dark
 * ground, i.e. an unreadable sign-in page for a large fraction of users. The
 * class is the app's own declared signal, so the backdrop moves when (and only
 * when) the text colours move with it, and it will be correct by construction
 * the day dark mode is actually wired up.
 *
 * ## Reduced motion
 *
 * Large, slow-drifting shapes are a textbook vestibular trigger, so every
 * animation is dropped under `prefers-reduced-motion: reduce`. The blobs still
 * render — the composition is the design; only the movement goes.
 *
 * The grain is static by design in BOTH modes. Animating it makes it shimmer,
 * which reads as video noise rather than as a surface, and it is the expensive
 * layer to repaint.
 */

/* ---------------------------------------------------------------------------
 * Geometry — proportional, because the prototype's lengths were a PHONE's
 *
 * Every length below is a RATIO of the design frame, never a px literal.
 * ------------------------------------------------------------------------- */

/**
 * The frame the prototype's absolute lengths were authored against: a phone.
 *
 * This is the fact a px transcription silently loses. Every length in the
 * prototype's CSS — 340px blobs, 70/90px of travel, a 58px blur — is a length
 * *on this frame*, i.e. a ratio that happened to be written out at one width.
 * Carried across as literals they stop meaning anything the moment the viewport
 * is not 375 wide: at 1024 a 340px blob is 33% of the frame where it was 91%.
 */
const FRAME = { w: 375, h: 812 } as const

/** The blur container's overhang per side, as a fraction of the viewport... */
const OVERHANG = 0.25
/** ...so the container spans 150% of the viewport on both axes. */
const CONTAINER_SPAN = 1 + 2 * OVERHANG

/**
 * The viewport-unit blend that replaces the px literals — the ONE unit every
 * other length here is a multiple of. `SCALE(1)` is 375px at the design frame.
 *
 * ## The criterion
 *
 * These blobs are a background WASH, and what is perceived of a wash is the
 * fraction of the FRAME it covers — not its width, not its height. Preserving
 * that means preserving blob area ÷ viewport area, i.e. scaling the diameter by
 * √(W·H): the geometric mean of the two axes.
 *
 * CSS has no portable √(vw·vh), so this approximates that geometric mean with a
 * linear blend of the two axes, fitted at two anchor frames (375×812, 1440×900).
 * It tracks the equal-area target within ~5% everywhere that matters — 320×568
 * (−2.5%), 768×1024 (−4.4%), 1440×900 (exact), 1920×1080 (+1.7%), 2560×1440
 * (+1.7%) — and overshoots only, and boundedly, at pathological aspect ratios
 * (+17% at 1920×600). It fails soft: a slightly large wash, never a broken
 * composition.
 *
 * ## Why not the obvious single units
 *
 * - **`vw`** — 1440×900 gives a 1306px blob, 145% of the viewport's HEIGHT. The
 *   three merge into one flat haze and the mid blob parks over the form.
 * - **`vh`** — 1440×900 gives 377px, 26% of the width. That is the bug being
 *   fixed here, barely moved.
 * - **`vmin` / `vmax`** — each keys to ONE axis and is blind to the other, which
 *   is the same class of error as a px literal, just less obvious. `vmax` hands
 *   1440×900 and 1440×400 an identical blob though the second frame has 44% of
 *   the area; `vmin` does the same to 375×812 and 375×2000. The blend is the
 *   only one of the four that answers to both axes at once.
 */
const VW_COEFF = 34.9
const VH_COEFF = 30.06

/** `ratio` of the design frame's width, as a viewport-relative CSS length. */
const SCALE = (ratio: number): string => {
  const w = ratio * VW_COEFF
  const h = ratio * VH_COEFF
  return `calc(${w.toFixed(4)}vw ${h < 0 ? '-' : '+'} ${Math.abs(h).toFixed(4)}vh)`
}

/**
 * The prototype's three blobs, transcribed EXACTLY as authored — px diameters
 * and travel at `FRAME`, edge anchors as the container percentages they were
 * written as, `c1`/`c2`/`c3`'s scale pulse and period. Nothing here is
 * re-derived; the derivation happens below, FROM these values, so the numbers a
 * designer would recognise stay legible next to the numbers the browser gets.
 */
const PROTO = [
  { d: 340, left: -0.14, top: 0.08, travel: [70, 90], scale: [1, 1.18], secs: 18 },
  { d: 340, right: -0.16, bottom: 0.06, travel: [-80, -60], scale: [1.1, 0.92], secs: 22 },
  { d: 260, left: 0.34, top: 0.46, travel: [-50, 70], scale: [1, 1], secs: 26 },
] as const

/**
 * How much of the prototype's blob SIZE to keep. 1 is the original panel.
 *
 * ## Why this exists: the platform's auth ground is far more restrained
 *
 * Spencer, looking at /sign-in: "let's have that match the platform login page
 * changes we made (we made it more subtle right?)". Measured across the two
 * surfaces rather than matched by eye — `SignInBackground.tsx` in `apps/platform`
 * is the reference, and the comparison has to be in RATIOS because the two were
 * authored on different frames (1280x800 there, 375x812 here):
 *
 * |                   | size / frame width | blur / own diameter |
 * |-------------------|--------------------|---------------------|
 * | platform (live)   | 0.31 / 0.28 / 0.25 | 0.17 / 0.20 / 0.20  |
 * | this, before      | 0.91 / 0.91 / 0.69 | 0.17 / 0.17 / 0.22  |
 *
 * So the BLUR relationship was already right; the gap was size relative to the
 * viewport — roughly 3x linearly, ~9x by area. That is the whole difference
 * between a ground that reads as three drifting accents and one that reads as a
 * full-bleed purple wash, and it is why only the sizes move here.
 *
 * ⚠️ The platform file's own docblock is STALE and will mislead you: it narrates
 * a change to "900/1300/640 at blur 200/300/170", but the live `BLOBS` there are
 * 400/360/320 at blur 69/72/64. The table above is read off the code, not that
 * comment. Do not re-derive this from the prose.
 *
 * ## Why a scalar rather than three new diameters
 *
 * The three `d` values above are the approved panel's and their RELATIVE sizes
 * are part of the composition. Retyping them as 117/105/94 would bury the
 * provenance and make the next adjustment a three-number edit with a chance of
 * drifting their proportions. One multiplier keeps the panel legible and makes
 * "more/less subtle" a single dial.
 *
 * 0.34 lands this surface on the platform's size ratios (0.31/0.31/0.24).
 *
 * ⚠️ Blur scales WITH it, and the first attempt at this got that wrong. Holding
 * the blur while the blobs shrink raises blur relative to diameter — at 0.34 the
 * fixed 58px blur becomes ~50% of a ~115px blob against the platform's ~19%, and
 * the measured result in the browser was a sign-in page with no visible
 * composition at all: a flat near-white ground with a lavender hint in two
 * corners. "More subtle" is not "gone".
 *
 * Scaling both together is what actually holds the reference: blur/diameter
 * stays at this panel's own 0.17, which is already where the platform sits
 * (0.17/0.20/0.20). So the blobs get smaller and keep reading as blobs.
 */
const SUBTLETY = 0.34

type Proto = (typeof PROTO)[number]

/**
 * A blob's CENTRE, as a fraction of the viewport, evaluated once at the design
 * frame.
 *
 * ## The half of the bug that is not about size
 *
 * The prototype positions each blob by an EDGE, as a percentage of a container
 * that is itself 150% of the viewport — so the offset is coupled to the viewport
 * twice over and grows with it, flinging the blobs outward. MEASURED at 1440×900
 * before this change: blob 0's centre sat at x = −492 and blob 1's at x = +1976,
 * each with its entire 340px diameter outside a 1440px viewport. Only blob 2 was
 * still on screen — the "I only see one blob" report, exactly.
 *
 * Enlarging the blobs alone would NOT have fixed that. An edge anchor holds its
 * distance from the edge proportional to the frame; a CENTRE holds the blob
 * where it was composed — blob 0 on the top-left corner, blob 1 just outside the
 * bottom-right, blob 2 near the middle — at every aspect ratio.
 *
 * Evaluating at `FRAME` is what makes 375×812 render identically to before: the
 * centres are, by construction, the exact centres the edge anchors produced
 * there. Verified against the browser — every blob's centre and diameter at the
 * design frame is unchanged to sub-pixel.
 */
const centreFraction = (p: Proto) => {
  const near = -OVERHANG
  const far = near + CONTAINER_SPAN
  const halfW = p.d / 2 / FRAME.w
  const halfH = p.d / 2 / FRAME.h
  return {
    x:
      'left' in p
        ? near + p.left * CONTAINER_SPAN + halfW
        : far - p.right * CONTAINER_SPAN - halfW,
    y:
      'top' in p
        ? near + p.top * CONTAINER_SPAN + halfH
        : far - p.bottom * CONTAINER_SPAN - halfH,
  }
}

/** A viewport fraction, re-expressed against the 150%-of-viewport container. */
const toContainerPct = (viewportFraction: number): string =>
  `${(((OVERHANG + viewportFraction) / CONTAINER_SPAN) * 100).toFixed(4)}%`

/**
 * The drift. 70-90px of travel AT THE DESIGN FRAME over 18-26s: slow enough
 * that the motion is felt rather than watched.
 *
 * The travel scales with the blobs, and has to — it is what makes the drift read
 * as the slow wander of a large shape. Left at 70px against a 700px blob it
 * would collapse into an imperceptible twitch, which is the same "lengths from
 * another frame" error as the diameters, just harder to see.
 */
const drift = (p: Proto) => keyframes`
  0%, 100% { transform: translate(0, 0) scale(${p.scale[0]}); }
  50%      { transform: translate(${SCALE(p.travel[0] / FRAME.w)}, ${SCALE(
    p.travel[1] / FRAME.w,
  )}) scale(${p.scale[1]}); }
`

/**
 * Everything one blob needs, all of it derived — no length here is hand-typed.
 *
 * The centre is applied as `left`/`top` plus a half-diameter negative margin
 * rather than a `translate(-50%, -50%)`, because `transform` is the drift's and
 * a centring transform would be overwritten by it the moment the animation runs.
 */
const BLOBS = PROTO.map((p) => {
  /*
    ⚠️ The centre is computed at the ORIGINAL diameter and only the RENDERED
    size is scaled — the order matters and the other one is wrong.

    `centreFraction` converts an edge anchor into a centre by adding half a
    diameter. Feed it a shrunken `d` and every blob's centre slides toward the
    edge it was anchored to: blob 0 further into the top-left corner, blob 1
    further out past the bottom-right, both losing most of what remained on
    screen while blob 2 sat alone in the middle. That is the same class of bug
    as the px-literal one this file was rewritten to fix, arriving from the
    other direction.

    Holding the centre and shrinking around it is what "the same composition,
    smaller" actually means: each blob stays exactly where the approved panel
    put it.
  */
  const c = centreFraction(p)
  const d = p.d * SUBTLETY
  return {
    diameter: SCALE(d / FRAME.w),
    offset: SCALE(-d / 2 / FRAME.w),
    left: toContainerPct(c.x),
    top: toContainerPct(c.y),
    animation: `${drift(p)} ${p.secs}s ease-in-out infinite`,
  }
})

/**
 * One purple family, deliberately. A warmer violet was tried during design and
 * rejected as reading too magenta — do not "warm these up".
 */
const BLOB_COLORS = {
  light: '#8F68EA',
  deep: '#4C2FA8',
  mid: '#6E4FD0',
} as const

/**
 * Colour and base opacity per blob, in the same order as `PROTO` — the paint,
 * where `PROTO` is the geometry. Split so a colour change and a size change are
 * never the same edit.
 */
const BLOB_LAYERS = [
  { color: BLOB_COLORS.light, opacity: 0.55 },
  { color: BLOB_COLORS.deep, opacity: 0.55 },
  { color: BLOB_COLORS.mid, opacity: 0.4 },
] as const

/** Base ground. Near-black rather than pure black so the grain has something to bite on. */
const BASE_DARK = '#0A0A0C'

/**
 * The light ground — faintly tinted, NOT pure white, and the reasoning runs the
 * opposite way to how it looks.
 *
 * ## Tinting SPENDS contrast budget; it cannot buy it
 *
 * The intuition is easy to get backwards, so the sign is worth stating plainly:
 * the field border is DARKER than the ground, which makes the GROUND the lighter
 * member of the contrast ratio. Darkening it shrinks the numerator. Measured on
 * /sign-in with no blob boost, against the ORIGINAL #8E8E93 border:
 *
 * | ground | bare | worst field border, 375 | worst, 1440 |
 * |---|---|---|---|
 * | `#FFFFFF` | 3.26:1 | 3.07:1 | 3.04:1 |
 * | `#FBFAFD` | 3.14:1 | **2.95:1** | **2.92:1** |
 *
 * This tint was therefore implemented and REVERTED once: on its own it put both
 * frames under WCAG 1.4.11 before a single blob was brightened. It ships now
 * only because the border moved first — #8E8E93 → #86868B in `fieldMetrics.ts`
 * buys ~0.10 of ground luminance, and that is what pays for this. The order is
 * load-bearing: this value without that border change is a regression.
 *
 * ## What the tint is FOR — it switches the grain back on
 *
 * Pure white is not a neutral choice here, it is a dead one. `overlay` blending
 * has gain 2·(1−base), so against #FFFFFF the grain contributes EXACTLY zero and
 * the light surface is flat, untextured white. MEASURED grain σ on bare ground:
 * 0.000 on `#FFFFFF` → 0.349 on `#FBFAFD`, against dark mode's 0.490. That is
 * the entire point of the value, and it is the same reason `BASE_DARK` is
 * #0A0A0C rather than pure black — a ground the texture can bite on.
 *
 * Do NOT "clean this up" back to #FFFFFF: it silently deletes the grain from
 * light mode and flattens the surface, with nothing in the render to say why.
 */
const BASE_LIGHT = '#FBFAFD'

/**
 * The blur, 58px at the design frame — and it SCALES with the blobs.
 *
 * It is an attribute of the blobs, not of the screen: it is applied to the blob
 * container alone, and its whole job is to make a hard-edged circle read as a
 * diffuse wash. Held at 58px while the blobs grow to ~700px it would stop doing
 * that job — the desktop composition would read as three crisper discs than the
 * phone one that was approved, which is the same "lengths from another frame"
 * error as the diameters and precisely as invisible.
 *
 * The grain deliberately does NOT scale with it. See the grain's own note.
 */
const BLUR_RATIO = (58 * SUBTLETY) / FRAME.w

/**
 * How much stronger each blob is in LIGHT mode, indexed as `PROTO` / `BLOB_LAYERS`.
 *
 * Light mode reads faint for a reason that is not a mistake: a translucent
 * purple over WHITE moves the ground much less than the same purple over
 * near-black, so the identical opacity buys visibly less presence. This is the
 * counterpart to `GRAIN_OPACITY_LIGHT` being *lower* than the dark one — same
 * asymmetry, opposite direction.
 *
 * Dark mode is APPROVED AS-IS and these must never touch it — hence a light-only
 * multiplier applied over `intensity`, rather than a change to the base opacities
 * that both modes share.
 *
 * ## ⚠️ PER-BLOB, because the cost is per-blob and wildly unequal
 *
 * A single scalar was tried first and is the wrong shape. Each blob sits over a
 * DIFFERENT element, with a different threshold and a different amount of slack,
 * so one number is forced down to whatever the unluckiest blob can bear:
 *
 * | blob           | sits over                    | threshold | slack |
 * |----------------|------------------------------|-----------|-------|
 * | 0 top-left     | Email field border           | 3:1       | wide  |
 * | 1 bottom-right | footer text                  | 4.5:1     | some  |
 * | 2 mid          | the "or" divider label       | 4.5:1     | NONE  |
 *
 * Blob 2 is pinned at 1.0 by one 16px word. "or" sits almost exactly on that
 * blob's centre at both frames, and it was ALREADY under 4.5:1 before any of this
 * work began (3.96:1 at 375, 4.22:1 at 1440, on the committed build). Brightening
 * the blob beneath already-failing text is not a trade worth making, so blob 2
 * does not move until that text does.
 *
 * Blobs 0 and 1 are the corner washes, they carry most of what the composition
 * reads as, and they have room — so the presence goes there. Measured ceilings
 * with the darker border and tinted ground in place: a UNIFORM gain is capped at
 * ~1.0 by "or" alone, while the field border tolerates 1.3+ (still 3.17:1). The
 * split is what turns a 0% boost into a real one.
 *
 * ## The rule these values were fitted to
 *
 * Two conditions, at BOTH 375×812 and 1440×900:
 *   1. every field border ≥ 3.00:1 against its composited ground (WCAG 1.4.11);
 *   2. no text item worse than the committed baseline at the same frame — the
 *      surface may not degrade legibility that already existed.
 *
 * Condition 2 is the binding one and the reason these are not larger. Re-run the
 * measurement rather than nudging by eye; the numbers move with the border token,
 * the ground tint and the blob geometry, all of which have changed under it.
 */
const LIGHT_GAIN = [1.5, 1.4, 1] as const

/**
 * Grain strength. Lower in light mode because `overlay` blending against white
 * is far more visible than against near-black — matched opacities would make
 * the light surface look dirty while the dark one looked flat.
 */
const GRAIN_OPACITY_LIGHT = 0.36
const GRAIN_OPACITY_DARK = 0.46

/** The selector the app uses for dark mode (`index.css`, `#vera-shadow-root.dark`). */
const DARK = '.dark &'

const blobBase = {
  position: 'absolute',
  borderRadius: '50%',
  /* The blobs are decorative shapes with no layout role, and taking them out of
     flow keeps the blurred container from being sized by them. */
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
} as const

export interface AuthBackdropProps {
  /**
   * Scales all three blob opacities, in BOTH modes. The default 1 is the
   * approved design. (`LIGHT_GAIN` stacks on top of this in light mode only.)
   *
   * The lever exists because legibility outranks the effect: if a future auth
   * surface puts low-contrast text (MUI's `text.secondary` is the one at risk)
   * directly over a blob core, dialling this down is the correct fix, not
   * lightening the text.
   *
   * Measure the composited contrast before choosing a value rather than
   * eyeballing it — and measure it against the COMPOSITE, not against the base
   * colour. The ground under this layer is white plus three blurred blobs plus
   * an overlay-blended grain, and no computed style reports what that sums to;
   * it has to come off a screenshot. Probe TEXT as well as field borders. They
   * have different thresholds (4.5:1 vs 3:1) and different worst locations, and
   * a blob that is free for one can be the binding constraint for the other —
   * see the price table on `LIGHT_GAIN`, where exactly that happened.
   */
  intensity?: number
}

export const AuthBackdrop: React.FC<AuthBackdropProps> = ({ intensity = 1 }) => {
  /* `useId` emits colons (`:r0:`), which are legal in an HTML id but break
     `url(#...)` references — strip them. Unique per instance so two mounted
     backdrops cannot capture each other's filter.

     Called before the gate's early return because hooks cannot be conditional. */
  const grainId = `vera-auth-grain-${useId().replace(/:/g, '')}`

  /* The white-label gate. Synchronous, so there is no frame in which a brand
     page shows Vera purple and then loses it. See the docblock. */
  if (!isVeraOwnedSurface()) return null

  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        /* Nothing here is interactive, and the layer covers the whole viewport —
           without this it would swallow every click on the form above it. */
        pointerEvents: 'none',
        /* The blob container is inset -25%, so it deliberately overhangs the
           viewport; this is what stops that overhang producing scrollbars. */
        overflow: 'hidden',
        backgroundColor: BASE_LIGHT,
        [DARK]: { backgroundColor: BASE_DARK },
      }}
    >
      {/*
        One blur on the container rather than one per blob: `filter` is the
        expensive property here, and blurring the composite also lets the three
        blobs bleed into each other instead of reading as three discs.

        The -25% inset pushes the blur's soft edge outside the viewport. Without
        it the blur would fade to transparent a few px inside the frame and draw
        a visible rectangle around the whole composition.
      */}
      <Box
        sx={{
          position: 'absolute',
          inset: `-${OVERHANG * 100}%`,
          filter: `blur(${SCALE(BLUR_RATIO)})`,
        }}
      >
        {BLOBS.map((b, i) => {
          const { color, opacity } = BLOB_LAYERS[i]
          return (
            <Box
              key={i}
              sx={{
                ...blobBase,
                width: b.diameter,
                height: b.diameter,
                left: b.left,
                top: b.top,
                /* Centring, kept off `transform` — that belongs to the drift. */
                marginLeft: b.offset,
                marginTop: b.offset,
                opacity: opacity * intensity * LIGHT_GAIN[i],
                [DARK]: { opacity: opacity * intensity },
                background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
                animation: b.animation,
              }}
            />
          )
        })}
      </Box>

      {/*
        Grain. `feTurbulence` generates its own pixels and ignores the source
        graphic, so the rect needs no fill; `feColorMatrix saturate=0` strips
        the colour noise the turbulence produces, leaving monochrome grain.

        `baseFrequency` 0.55 is the COARSE setting from the prototype — higher
        values give a finer, more digital-looking noise that disappears on a
        retina phone. `stitchTiles` keeps tile edges from showing as seams.

        ## The grain does NOT scale with the blobs — deliberately

        `baseFrequency` is a frame-relative length like the others (0.55 ≈ a
        1.8px feature), so the question had to be answered rather than inherited.
        The blur scales and this does not, and the asymmetry is the point:

        - The blur belongs to the BLOBS. It is applied to their container and its
          job is to keep a circle reading as a wash, so it has to track them.
        - The grain belongs to the SURFACE, and the surface's visual partners are
          the things it sits behind — 16px text, 1px borders, a 40px field — none
          of which scale with the viewport. Grain that grew with the frame would
          be ~7px at 1440 wide, which is mottling, not grain, and it would drift
          out of step with every fixed-size element on top of it.

        The film-grain intuition (enlarge the print, enlarge the grain) does not
        apply, because the print is not being enlarged here — only the blobs are.

        ## Known, measured: in LIGHT mode this layer does nothing over white

        `overlay` preserves the extremes — its gain is 2·(1−base) above mid — so
        as the ground approaches white the grain's contribution goes to zero.
        MEASURED on /sign-in at 1440×900: grain σ = 0.000 over the white field,
        0.42 over a pale wash, 1.11 over the deepest one. Dark mode gets a
        uniform σ ≈ 0.49 everywhere, which is why only the light surface reads as
        flat. That is a real part of why light mode looks faint, and it cannot be
        fixed by opacity: every blend mode that survives a white ground also
        darkens it, which is budget this surface does not have (see LIGHT_GAIN).

        CONFIRMED by experiment: on a `#FBFAFD` ground the same layer measures
        σ = 0.349 on bare ground — from nothing to ~71% of dark mode's 0.490. So
        the fix for the flatness is a ground that is not pure white, and it works.
        It is not shipped only because that ground costs more field contrast than
        this surface has; see `BASE_LIGHT` for the measurements that blocked it.
      */}
      <Box
        component="svg"
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          mixBlendMode: 'overlay',
          opacity: GRAIN_OPACITY_LIGHT,
          [DARK]: { opacity: GRAIN_OPACITY_DARK },
        }}
      >
        <filter id={grainId}>
          <feTurbulence type="fractalNoise" baseFrequency="0.55" numOctaves="4" stitchTiles="stitch" />
          <feColorMatrix type="saturate" values="0" />
        </filter>
        <rect width="100%" height="100%" filter={`url(#${grainId})`} />
      </Box>
    </Box>
  )
}
